import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { api, fmt, monthLabel } from "@/lib/api";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp } from "lucide-react";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[#0a0e14] border border-[#2a364f] px-3 py-2 text-xs shadow-xl">
      <div className="font-medium mb-1 text-slate-200">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 font-mono-num" style={{ color: p.color || p.fill }}>
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

export default function Trends() {
  const { month } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["trends", month], queryFn: () => api.getTrends(month) });

  const series = (data?.series || []).map((s) => ({ ...s, label: monthLabel(s.month) }));
  const breakdown = data?.breakdown || [];
  const totalBreakdown = breakdown.reduce((a, b) => a + b.value, 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-[#f97316]" /> Trends
        </h2>
        <p className="text-slate-400 text-sm mt-1">6-month budget vs. actual and category breakdown.</p>
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">Loading charts…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div data-testid="budget-vs-actual-chart" className="lg:col-span-7 rounded-2xl bg-[#1a2232] border border-[#2a364f] p-5">
            <h3 className="font-heading font-semibold mb-4">Budget vs. Actual</h3>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a364f" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(148,163,184,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                <Bar dataKey="budget" name="Budget" fill="#f97316" radius={[6, 6, 0, 0]} maxBarSize={38} />
                <Line type="monotone" dataKey="actual" name="Actual" stroke="#22c55e" strokeWidth={3} dot={{ r: 4, fill: "#22c55e" }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div data-testid="category-breakdown-chart" className="lg:col-span-5 rounded-2xl bg-[#1a2232] border border-[#2a364f] p-5">
            <h3 className="font-heading font-semibold mb-1">Category Breakdown</h3>
            <p className="text-xs text-slate-500 mb-2">{monthLabel(month)} · {fmt(totalBreakdown)} total</p>
            {breakdown.length === 0 ? (
              <div className="py-20 text-center text-slate-500 text-sm">No spending this month.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={2} stroke="none">
                    {breakdown.map((b, i) => (
                      <Cell key={i} fill={b.color || "#a855f7"} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
